import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { formatFunnelCount, formatFunnelPercent } from '../../helpers/funnelChart';

// One hue, darkening with the size of the step, because these are magnitudes of
// the SAME measurement — profiles — at successive stages. The categorical palette
// this used to cycle through (Colors.jsx: amethyst, blue, pink, teal…) gave each
// stage an unrelated hue, which reads as four different things being compared
// rather than one population draining, and left the colour saying nothing at all.
//
// Six shades because MAX_FUNNEL_STEPS is 6 (helpers/funnels.js). Darkest first, so
// weight tracks the count down the funnel instead of contradicting it.
const STEP_SHADES = [
  'var(--mantine-color-primary-8)',
  'var(--mantine-color-primary-7)',
  'var(--mantine-color-primary-6)',
  'var(--mantine-color-primary-5)',
  'var(--mantine-color-primary-4)',
  'var(--mantine-color-primary-3)',
];

// The funnel is drawn in its own coordinate space and stretched to whatever width
// the card gives it, so these are ratios rather than pixels.
const VIEW_WIDTH = 1000;
const CENTRE = VIEW_WIDTH / 2;
// A stage at 0.1% would otherwise be a hairline. Small enough to stay honest at
// this scale (1.2% of the width) while keeping the shape continuous.
const MIN_HALF_WIDTH = 6;
// Row height is taken from the caller's `height` where it divides sensibly, but
// bounded: too short and the bands stop reading as a shape, too tall and a
// two-step funnel becomes a wall.
const MIN_ROW = 28;
const MAX_ROW = 52;

/**
 * Draws an ordered funnel: one contiguous tapering band per step.
 *
 * WHY A SILHOUETTE. This started as horizontal bars, then bars on grey tracks —
 * and the feedback on that was that it did not look like a funnel, which was
 * right. Separated bars with a track behind them are a bullet chart: the eye reads
 * each row against its own track, one row at a time, and nothing connects them.
 * A funnel is one continuous shape, and the SLOPE between two stages is the drop
 * between them. Bands here touch, and each band's top edge is its own count while
 * its bottom edge is the next step's, so the taper is the loss and a step that
 * holds its audience draws as a near-rectangle.
 *
 * WIDTH IS THE COUNT, as a share of the largest step. An earlier version scaled
 * bars by each step's conversion from the one above, so a 13.4K Purchase step drew
 * a LONGER bar than the 43.0K Add-to-cart step above it, with both counts printed
 * right there. Encoding one quantity as length while labelling the shape with a
 * different one asks the reader to distrust either the bar or the number.
 *
 * WHY A STEEP FUNNEL STILL LOOKS STEEP. A log width was raised again for exactly
 * the right reason — 179K to 13K draws a cliff, and a cliff looks like a broken
 * chart. It is still rejected: width would go on reading as a count while a 1000x
 * drop rendered as a 3x one, so the shape would be legible and wrong, which is
 * worse than illegible and right. A square root has the same defect in milder
 * form; it is only an honest encoding when the band's AREA carries the value, and
 * these bands are a fixed height.
 *
 * What makes a steep funnel read as intentional instead is naming the empty space.
 * The pale envelope behind the shape is the first step's width held all the way
 * down, so the gap between it and the funnel is the people lost since the top — a
 * quantity worth seeing rather than blank card. A steep funnel then draws as a
 * large, obvious loss, which is what it is.
 *
 * Names, counts and rates sit in fixed columns either side of the shape rather
 * than inside it, so they line up down the card whatever the band's width — and so
 * a 1% step still shows its numbers.
 *
 * Steps are drawn exactly as given — ordering and whitelisting are the caller's
 * job, so this renders a user-defined funnel as readily as the fixed
 * email/campaign one (see buildEmailFunnelSteps in helpers/funnelChart.js).
 *
 * @param {Array<{label: string, value: number, percentOfPrevious: number|null}>} data - steps in flow order
 * @param {number} [height]
 */
const FunnelChart = ({ data, height = 300 }) => {
  if (!data || data.length === 0) return null;

  // Against the largest step rather than the first: a funnel is normally
  // monotonic, so they are the same number — but a non-monotonic sequence (a later
  // step counting more than an earlier one, which user-defined steps permit) would
  // otherwise compute a width above 100% and overflow the viewBox.
  const largest = Math.max(...data.map((item) => item.value || 0));
  const rowHeight = Math.max(MIN_ROW, Math.min(MAX_ROW, Math.floor(height / data.length)));
  const chartHeight = rowHeight * data.length;

  const halfWidthOf = (value) =>
    largest > 0 ? Math.max((((value || 0) / largest) * VIEW_WIDTH) / 2, MIN_HALF_WIDTH) : MIN_HALF_WIDTH;
  const percentOf = (value) => (largest > 0 ? ((value || 0) / largest) * 100 : 0);

  return (
    <Group align="flex-start" gap="xs" wrap="nowrap" style={{ minHeight: chartHeight }}>
      <Stack gap={0} w={110} style={{ flexShrink: 0 }}>
        {data.map((item, index) => (
          <Tooltip
            // Step names are user-defined on a custom funnel, so two of them can
            // legitimately read the same; the position is what identifies a step.
            // The tooltip is on the name because the name is what gets truncated.
            key={index}
            label={
              <div>
                <div>
                  <strong>{item.label}</strong>
                </div>
                <div>{(item.value || 0).toLocaleString()}</div>
                {index > 0 && <div>Conversion: {formatFunnelPercent(item.percentOfPrevious)}</div>}
              </div>
            }
            position="right"
          >
            <Box h={rowHeight} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {/* Truncated, not wrapped: a user-typed name of any length has to
                  leave the row one line high, or it pushes its band out of line
                  with the shape beside it. */}
              <Text size="xs" c="dimmed" ta="right" truncate>
                {item.label}
              </Text>
            </Box>
          </Tooltip>
        ))}
      </Stack>

      {/* preserveAspectRatio="none" because the widths are ratios and the height is
          already fixed in px — the shape is meant to stretch to the card, and the
          bands must stay exactly rowHeight tall to line up with the columns. */}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <svg
          width="100%"
          height={chartHeight}
          viewBox={`0 0 ${VIEW_WIDTH} ${chartHeight}`}
          preserveAspectRatio="none"
          role="presentation"
        >
          {/* Everyone the funnel started with, held down the whole chart. The gap
              between this and the shape is the cumulative drop-off, which is what
              stops a narrow spout reading as a rendering fault. Drawn once, as a
              single envelope, rather than as a track per row — per-row tracks are
              what made the previous attempt read as a bullet chart, where the eye
              takes each row on its own and nothing connects them. */}
          <rect
            data-funnel-envelope
            x={CENTRE - halfWidthOf(data[0].value)}
            y={0}
            width={halfWidthOf(data[0].value) * 2}
            height={chartHeight}
            fill="var(--mantine-color-default-border)"
            opacity={0.45}
          />
          {data.map((item, index) => {
            const top = halfWidthOf(item.value);
            // The last band has no next step to taper towards, so it closes as a
            // rectangle — a spout — rather than pinching to a point, which would
            // read as "and then everybody left".
            const bottom = index + 1 < data.length ? halfWidthOf(data[index + 1].value) : top;
            const y = index * rowHeight;

            return (
              <polygon
                key={index}
                // The encoding, stated as a number so a test can assert it without
                // parsing path geometry.
                data-funnel-band
                data-width-percent={percentOf(item.value).toFixed(2)}
                points={[
                  `${CENTRE - top},${y}`,
                  `${CENTRE + top},${y}`,
                  `${CENTRE + bottom},${y + rowHeight}`,
                  `${CENTRE - bottom},${y + rowHeight}`,
                ].join(' ')}
                fill={STEP_SHADES[index % STEP_SHADES.length]}
                stroke="var(--mantine-color-body)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </svg>
      </Box>

      {/* Fixed columns, every row the same. Counts used to sit inside the bar when
          it was wide enough and outside when it was not, so the first step's count
          appeared in a different place from every other step's. */}
      <Stack gap={0} w={56} style={{ flexShrink: 0 }}>
        {data.map((item, index) => (
          <Box key={index} h={rowHeight} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Text size="xs" fw={600}>
              {formatFunnelCount(item.value)}
            </Text>
          </Box>
        ))}
      </Stack>
      <Stack gap={0} w={50} style={{ flexShrink: 0 }}>
        {data.map((item, index) => (
          <Box key={index} h={rowHeight} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Text size="xs" c="dimmed">
              {index > 0 ? formatFunnelPercent(item.percentOfPrevious) : ''}
            </Text>
          </Box>
        ))}
      </Stack>
    </Group>
  );
};

export default FunnelChart;
